import { useState, useEffect, useRef } from 'react';
import type { Map } from 'mapbox-gl';
import type { HotspotFeatureCollection } from 'ts-core';
import { useApi } from 'react-ui';
import { apiFetch } from 'ts-core';

const HOTSPOT_FETCH_DEBOUNCE_MS = 250;

export const HOTSPOT_MAX_ZOOM = parseInt(
  import.meta.env.VITE_HOTSPOT_MAX_ZOOM ?? '12',
  10
);

/** Controls which hotspot layer variant is rendered: sized cluster circles or small per-cell dots. */
export const HOTSPOT_DISPLAY_MODE = (
  import.meta.env.VITE_HOTSPOT_DISPLAY ?? 'dots'
) as 'clusters' | 'dots';

/** Provider short-names excluded from both the hotspot and search queries. */
export const EXCLUDED_PROVIDERS: string[] = ['OET'];

interface UseMapHotspotsReturn {
  data: HotspotFeatureCollection | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Fetches hotspot points from GET /api/v2/map/hotspots scoped to the current
 * map viewport. It sends query params in the same format as /api/search:
 * - topLeft=lat,lng (NW corner)
 * - bottomRight=lat,lng (SE corner)
 * - providers=A,B,C (comma-separated allow-list; omit to show all)
 *
 * Requests are re-triggered on map drag/zoom end with a short debounce.
 *
 * @param map       - The Mapbox GL map instance.
 * @param providers - Allow-list of provider short-names to include.
 * @param tags      - Allow-list of tag slugs the balloon must carry (empty = no tag filter).
 *                    Currently set by the layer components only when a saved-search-bound
 *                    tag has forceMapClustering enabled, so the dot density underneath the
 *                    cluster balloons reflects the same filter.
 */
export function useMapHotspots(map: Map | null, providers: string[] = [], tags: string[] = [], user: string[] = []): UseMapHotspotsReturn {
  const { apiEndpoints, apiKey } = useApi();

  const [data, setData] = useState<HotspotFeatureCollection | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const apiEndpointRef = useRef(apiEndpoints.mapHotspots);
  const apiKeyRef = useRef(apiKey);

  useEffect(() => {
    apiEndpointRef.current = apiEndpoints.mapHotspots;
  }, [apiEndpoints.mapHotspots]);

  useEffect(() => {
    apiKeyRef.current = apiKey;
  }, [apiKey]);

  useEffect(() => {
    if (!map || providers.length === 0) return;

    const fetchHotspots = async (): Promise<void> => {
      const bounds = map.getBounds();
      if (!bounds) return;

      const northWest = bounds.getNorthWest();
      const southEast = bounds.getSouthEast();

      const params = new URLSearchParams();
      params.append('topLeft', `${northWest.lat.toFixed(4)},${northWest.lng.toFixed(4)}`);
      params.append('bottomRight', `${southEast.lat.toFixed(4)},${southEast.lng.toFixed(4)}`);

      if (providers.length > 0) {
        params.append('providers', providers.join(','));
      }

      if (tags.length > 0) {
        params.append('tags', tags.join(','));
      }

      if (user.length > 0) {
        params.append('user', user.join(','));
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      setLoading(true);
      setError(null);

      const separator = apiEndpointRef.current.includes('?') ? '&' : '?';
      const url = `${apiEndpointRef.current}${separator}${params.toString()}`;

      try {
        const response = await apiFetch(url, {
          method: 'GET',
          apiKey: apiKeyRef.current,
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch hotspots: ${response.status} ${response.statusText}`);
        }

        const json: HotspotFeatureCollection = await response.json();
        setData(json);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setLoading(false);
      }
    };

    const scheduleFetch = (): void => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        void fetchHotspots();
      }, HOTSPOT_FETCH_DEBOUNCE_MS);
    };

    map.on('zoomend', scheduleFetch);
    map.on('dragend', scheduleFetch);

    void fetchHotspots();

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      map.off('zoomend', scheduleFetch);
      map.off('dragend', scheduleFetch);
      abortControllerRef.current?.abort();
    };
  }, [map, providers, tags, user]);

  return { data, loading, error };
}
